import * as React from "react"

import { cn } from "../../cn"
import { useCopyToClipboardFeedback } from "../../hooks/useCopyToClipboardFeedback"
import { CopyAnnouncement } from "../CopyButton/CopyAnnouncement"
import { Tooltip } from "../Tooltip/Tooltip"

export interface CopyableIdProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value"
> {
  /** The full identifier. Copied to the clipboard in full on click. */
  id: string
  /** Number of leading characters to display. Defaults to 4. */
  chars?: number
  /** Hover tooltip text, and the action in the accessible name. Defaults to "Copy ID". */
  label?: string
  /** Tooltip text shown after a successful copy. Defaults to "Copied!". */
  copiedLabel?: string
}

/**
 * Standardized table ID cell. Displays the first `chars` characters of an id
 * (4 by default, no dots or trailing characters). Hover brightens the characters
 * and shows the copy hint; clicking copies the full id to the clipboard and
 * holds a confirmation tooltip that clears itself.
 */
const CopyableId = React.forwardRef<HTMLButtonElement, CopyableIdProps>(
  (
    {
      id,
      chars = 4,
      label = "Copy ID",
      copiedLabel = "Copied!",
      className,
      onClick,
      "aria-label": ariaLabel,
      ...props
    },
    ref,
  ) => {
    const { copied, copy } = useCopyToClipboardFeedback()

    const short = id.slice(0, chars)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      onClick?.(event)
      void copy(id)
    }

    return (
      <>
        <Tooltip content={copied ? copiedLabel : label} forceOpen={copied}>
          <button
            type="button"
            ref={ref}
            onClick={handleClick}
            className={cn(
              // Shrinks to the space it is given and truncates the ID inside,
              // so in a narrow column the button — and the focus ring drawn
              // around it — stays within the column's clip.
              "inline-flex min-w-0 max-w-full items-center justify-center",
              "font-mono text-sm text-coval-text-muted transition-colors hover:text-coval-text",
              "rounded-sm",
              className,
            )}
            {...props}
            // The name leads with the characters on screen, so a speech-input
            // user can say what they see (WCAG 2.5.3); the action, or a
            // caller's `aria-label`, follows.
            aria-label={`${short}, ${ariaLabel ?? label}`}
          >
            <span className="truncate">{short}</span>
          </button>
        </Tooltip>
        <CopyAnnouncement message={copied ? copiedLabel : null} />
      </>
    )
  },
)

CopyableId.displayName = "CopyableId"

export { CopyableId }
